import { Component } from '@theme/component';

export const FLOORING_ROOM_LIMIT = 20;
export const SQFT_PER_SQM = 1 / (0.3048 * 0.3048);
const FEET_PER_METRE = 1 / 0.3048;
const MODES = ['area', 'dims', 'rooms'];
const UNITS = ['imperial', 'metric'];
const WASTE_OPTIONS = [0, 5, 10, 15, 20];

/** Convert a measurement without changing the area it represents. */
export function convertFlooringMeasurement(value, kind, from, to) {
  if (from === to || value === null || !Number.isFinite(value)) return value;
  const factor = kind === 'area' ? SQFT_PER_SQM : FEET_PER_METRE;
  return to === 'metric' ? value / factor : value * factor;
}

function positive(value) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function validRoom(room) {
  return room?.mode === 'area'
    ? positive(room.area)
    : room?.mode === 'dims' && positive(room.length) && positive(room.width);
}

/** Sum raw room areas, apply waste once, and round the current model to whole boxes. */
export function calculateFlooringProject(project, model) {
  const coverage = Number(model.coveragePerBox);
  const planks = Number(model.planksPerBox);
  const price = Number(model.priceCents);
  const state = {
    version: 1,
    handle: project.handle,
    mode: project.mode,
    units: project.units,
    waste: project.waste,
    rooms: Array.isArray(project.rooms) ? project.rooms.map((room) => ({ ...room })) : [],
    areaSqft: null,
    requiredSqft: null,
    boxes: null,
    coveragePerBox: positive(coverage) ? coverage : null,
    planksPerBox: Number.isSafeInteger(planks) && planks > 0 ? planks : null,
    totalCoverageSqft: null,
    priceCents: Number.isSafeInteger(price) && price > 0 ? price : 0,
    valid: false,
  };
  const count = state.rooms.length;
  if (!MODES.includes(state.mode) || !UNITS.includes(state.units) || !WASTE_OPTIONS.includes(state.waste)) return state;
  if (count < 1 || count > FLOORING_ROOM_LIMIT || (state.mode !== 'rooms' && count !== 1)) return state;
  if (state.mode !== 'rooms' && state.rooms[0].mode !== state.mode) return state;
  if (!state.rooms.every(validRoom) || !positive(coverage)) return state;
  const rawArea = state.rooms.reduce((total, room) => total + (room.mode === 'area' ? room.area : room.length * room.width), 0);
  const area = state.units === 'metric' ? rawArea * SQFT_PER_SQM : rawArea;
  const needed = area * (1 + state.waste / 100);
  const ratio = needed / coverage;
  if (!positive(area) || !positive(needed) || !positive(ratio)) return state;
  const tolerance = Number.EPSILON * 8 * Math.max(1, Math.abs(ratio));
  const boxes = Math.max(1, Math.ceil(ratio - tolerance));
  const totalCoverage = boxes * coverage;
  if (!Number.isSafeInteger(boxes) || !positive(totalCoverage)) return state;
  Object.assign(state, { areaSqft: area, requiredSqft: needed, boxes, totalCoverageSqft: totalCoverage, valid: true });
  return state;
}

/** Saved totals are ignored; only valid measurements for this model are restored. */
export function restorableFlooringProject(saved, handle, model) {
  if (!saved || saved.version !== 1 || saved.valid !== true || saved.handle !== handle) return null;
  if (!MODES.includes(saved.mode) || !UNITS.includes(saved.units) || !WASTE_OPTIONS.includes(saved.waste)) return null;
  if (!Array.isArray(saved.rooms) || saved.rooms.length < 1 || saved.rooms.length > FLOORING_ROOM_LIMIT) return null;
  const rooms = [];
  for (const room of saved.rooms) {
    if (!room || !['area', 'dims'].includes(room.mode) || typeof room.name !== 'string' || room.name.length > 80) return null;
    const clean = { name: room.name, mode: room.mode };
    for (const key of ['area', 'length', 'width']) {
      const value = room[key];
      if (value !== null && (typeof value !== 'number' || !Number.isFinite(value))) return null;
      clean[key] = value;
    }
    rooms.push(clean);
  }
  const project = { handle, mode: saved.mode, units: saved.units, waste: saved.waste, rooms };
  return calculateFlooringProject(project, model).valid ? project : null;
}

export class CavadorFlooringCalculator extends Component {
  #controller;
  #root;
  #text = {};
  #mode = 'area';
  #units = 'imperial';
  #roomSequence = 0;
  #restore;
  #emptyText = '';
  #draft = null;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    if (this.#root?.restoreCavadorFlooringCalculation === this.#restore) delete this.#root.restoreCavadorFlooringCalculation;
  }

  #initialize() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#root = this.closest('[data-flooring-calculator]');
    if (!this.#root) return;
    const draft = this.#draft?.handle === this.#root.dataset.handle ? this.#draft : null;
    try {
      this.#text = JSON.parse(this.querySelector('[data-calculator-text]')?.textContent || '{}');
    } catch {
      return;
    }
    this.#emptyText = this.querySelector('[data-empty]')?.textContent || '';
    this.#mode = this.querySelector('[data-mode-btn].is-active')?.dataset.modeBtn || 'area';
    this.#units = this.querySelector('[data-units].is-active')?.dataset.units || 'imperial';
    const waste = this.querySelector('[data-waste-select]');
    if (waste) waste.value = WASTE_OPTIONS.includes(Number(this.#root.dataset.waste)) ? this.#root.dataset.waste : '10';
    if (draft) {
      this.#mode = draft.mode;
      this.#units = draft.units;
      if (waste) waste.value = String(draft.waste);
      for (const key of ['area', 'length', 'width']) this.querySelector(`[data-${key}]`).value = draft.single[key] ?? '';
      this.querySelector('[data-rooms]').replaceChildren();
      for (const room of draft.rooms) this.#addRoom(room);
    }
    if (!this.querySelector('[data-room]')) this.#addRoom();
    const signal = this.#controller.signal;
    this.addEventListener('input', this.#onInput, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    this.querySelector('.aurora-calc__cart')?.addEventListener('submit', this.#onSubmit, { signal });
    this.#restore = (state) => this.#restoreProject(state);
    this.#root.restoreCavadorFlooringCalculation = this.#restore;
    this.#syncControls();
    this.#compute();
  }

  #model() {
    return {
      coveragePerBox: Number(this.#root.dataset.coverage),
      planksPerBox: Number(this.#root.dataset.planks),
      priceCents: Number(this.#root.dataset.price),
    };
  }

  #defaultRoomName(number) {
    return this.#interpolate(this.#text.room_name, { number });
  }

  #interpolate(text, values) {
    return String(text || '').replace(/__(\w+)__/g, (_, key) => String(values[key.toLowerCase()] ?? ''));
  }

  #value(selector, parent = this) {
    const input = parent.querySelector(selector);
    if (!input || input.value.trim() === '' || input.validity?.badInput) return null;
    const number = Number(input.value);
    return Number.isFinite(number) ? number : null;
  }

  #roomState(row, index) {
    return {
      name: row.querySelector('[data-room-name]').value.trim() || this.#defaultRoomName(index + 1),
      mode: row.querySelector('[data-room-mode]').value,
      area: this.#value('[data-room-area]', row),
      length: this.#value('[data-room-length]', row),
      width: this.#value('[data-room-width]', row),
    };
  }

  #project() {
    const rooms = this.#mode === 'rooms'
      ? [...this.querySelectorAll('[data-room]')].map((row, index) => this.#roomState(row, index))
      : [{
          name: this.#defaultRoomName(1),
          mode: this.#mode,
          area: this.#value('[data-area]'),
          length: this.#value('[data-length]'),
          width: this.#value('[data-width]'),
        }];
    return { handle: this.#root.dataset.handle, mode: this.#mode, units: this.#units, waste: Number(this.querySelector('[data-waste-select]').value), rooms };
  }

  #onInput = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (event.target.matches('[data-room-name]')) this.#syncRoom(event.target.closest('[data-room]'));
    this.#compute();
  };

  #onChange = (event) => {
    if (!(event.target instanceof HTMLSelectElement)) return;
    if (event.target.matches('[data-room-mode]')) this.#syncRoom(event.target.closest('[data-room]'));
    this.#compute();
  };

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('button');
    if (!button || !this.contains(button)) return;
    if (button.matches('[data-mode-btn]')) {
      this.#mode = button.dataset.modeBtn;
      this.#syncControls();
    } else if (button.matches('[data-units]')) {
      this.#changeUnits(button.dataset.units);
    } else if (button.matches('[data-add-room]')) {
      const room = this.#addRoom();
      room?.querySelector('[data-room-name]')?.focus();
    } else if (button.matches('[data-remove-room]')) {
      const row = button.closest('[data-room]');
      const rows = [...this.querySelectorAll('[data-room]')];
      if (rows.length <= 1) return;
      const index = rows.indexOf(row);
      row.remove();
      const next = this.querySelectorAll('[data-room]')[Math.min(index, rows.length - 2)];
      next?.querySelector('[data-room-name]')?.focus();
      this.#syncRoomLimit();
    } else {
      return;
    }
    this.#compute();
  };

  #onKeydown = (event) => {
    if (!(event.target instanceof Element) || !event.target.matches('[data-mode-btn]')) return;
    const tabs = [...this.querySelectorAll('[data-mode-btn]')];
    const index = tabs.indexOf(event.target);
    let next;
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
    if (event.key === 'ArrowLeft') next = (index + tabs.length - 1) % tabs.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = tabs.length - 1;
    if (next === undefined) return;
    event.preventDefault();
    this.#mode = tabs[next].dataset.modeBtn;
    this.#syncControls();
    tabs[next].focus();
    this.#compute();
  };

  #onSubmit = (event) => {
    this.#compute();
    if (!this.#root.cavadorFlooringCalculation?.valid || this.#model().priceCents <= 0) event.preventDefault();
  };

  #addRoom(saved) {
    const list = this.querySelector('[data-rooms]');
    if (!list || list.children.length >= FLOORING_ROOM_LIMIT) return null;
    const template = this.querySelector('[data-room-template]');
    const fragment = template.content.cloneNode(true);
    const row = fragment.querySelector('[data-room]');
    const id = `${this.#root.id}-room-${++this.#roomSequence}`;
    const error = row.querySelector('[data-room-error]');
    error.id = `${id}-error`;
    for (const input of row.querySelectorAll('[data-room-field]')) {
      input.id = `${id}-${input.dataset.roomField}`;
      const label = row.querySelector(`[data-label-for="${input.dataset.roomField}"]`);
      label?.setAttribute('for', input.id);
      if (input instanceof HTMLInputElement && input.type === 'number') input.setAttribute('aria-describedby', error.id);
    }
    row.querySelector('[data-room-name]').value = saved?.name || this.#defaultRoomName(list.children.length + 1);
    row.querySelector('[data-room-mode]').value = saved?.mode || 'area';
    for (const key of ['area', 'length', 'width']) row.querySelector(`[data-room-${key}]`).value = saved?.[key] ?? '';
    list.append(fragment);
    this.#syncRoom(row);
    this.#syncControls();
    return row;
  }

  #syncRoom(row) {
    if (!row) return;
    const index = [...this.querySelectorAll('[data-room]')].indexOf(row);
    const room = this.#roomState(row, index);
    row.querySelector('[data-room-title]').textContent = room.name;
    row.querySelector('[data-remove-room]').setAttribute('aria-label', this.#interpolate(this.#text.remove_room, { name: room.name }));
    for (const panel of row.querySelectorAll('[data-room-panel]')) panel.hidden = panel.dataset.roomPanel !== room.mode;
  }

  #syncRoomLimit() {
    const count = this.querySelectorAll('[data-room]').length;
    const add = this.querySelector('[data-add-room]');
    if (add) add.disabled = count >= FLOORING_ROOM_LIMIT;
    const limit = this.querySelector('[data-room-limit]');
    if (limit) {
      limit.hidden = count < FLOORING_ROOM_LIMIT;
      limit.textContent = this.#text.room_limit;
    }
    for (const remove of this.querySelectorAll('[data-remove-room]')) remove.disabled = count <= 1;
  }

  #syncControls() {
    for (const tab of this.querySelectorAll('[data-mode-btn]')) {
      const active = tab.dataset.modeBtn === this.#mode;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    }
    for (const panel of this.querySelectorAll('[data-panel]')) {
      panel.hidden = panel.dataset.panel !== this.#mode;
      panel.classList.toggle('is-hidden', panel.hidden);
    }
    for (const button of this.querySelectorAll('[data-units]')) {
      const active = button.dataset.units === this.#units;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    }
    for (const label of this.querySelectorAll('[data-unit-area]')) label.textContent = this.#units === 'metric' ? this.#text.square_metres : this.#text.square_feet;
    for (const label of this.querySelectorAll('[data-unit-len], [data-unit-wid]')) label.textContent = this.#units === 'metric' ? this.#text.metres : this.#text.feet;
    this.#syncRoomLimit();
  }

  #changeUnits(next) {
    if (!UNITS.includes(next) || next === this.#units) return;
    for (const input of this.querySelectorAll('input[type="number"]')) {
      if (input.value.trim() === '' || input.validity.badInput) continue;
      const value = Number(input.value);
      if (!Number.isFinite(value)) continue;
      const kind = input.matches('[data-area], [data-room-area]') ? 'area' : 'length';
      input.value = String(convertFlooringMeasurement(value, kind, this.#units, next));
    }
    this.#units = next;
    this.#syncControls();
  }

  #number(value, digits = 1) {
    return new Intl.NumberFormat(this.#root.dataset.locale || 'en', { maximumFractionDigits: digits, minimumFractionDigits: digits }).format(value);
  }

  #compute() {
    const project = this.#project();
    const state = calculateFlooringProject(project, this.#model());
    const rooms = [...this.querySelectorAll('[data-room]')];
    rooms.forEach((row, index) => {
      const room = this.#roomState(row, index);
      const invalid = this.#mode === 'rooms' && !validRoom(room);
      const error = row.querySelector('[data-room-error]');
      error.hidden = !invalid;
      error.textContent = this.#text.invalid_room;
      row.classList.toggle('is-invalid', invalid);
      for (const input of row.querySelectorAll('input[type="number"]')) {
        const active = room.mode === 'area' ? input.matches('[data-room-area]') : input.matches('[data-room-length], [data-room-width]');
        input.setAttribute('aria-invalid', String(invalid && active && !positive(this.#value(`[data-room-${input.dataset.roomField}]`, row))));
      }
    });
    for (const input of this.querySelectorAll('[data-area], [data-length], [data-width]')) {
      const active = this.#mode === 'area' ? input.matches('[data-area]') : this.#mode === 'dims' && input.matches('[data-length], [data-width]');
      input.setAttribute('aria-invalid', String(active && !positive(this.#value(`[${input.matches('[data-area]') ? 'data-area' : input.matches('[data-length]') ? 'data-length' : 'data-width'}]`))));
    }
    const stats = this.querySelector('[data-stats]');
    stats.classList.toggle('is-hidden', !state.valid);
    const empty = this.querySelector('[data-empty]');
    empty.classList.toggle('cavador-multiroom__status-summary', state.valid);
    empty.textContent = state.valid
      ? this.#interpolate(this.#text.estimate, { area: this.#number(state.areaSqft), waste: state.waste, coverage: this.#number(state.totalCoverageSqft) })
      : this.#mode === 'rooms' ? this.#text.complete_rooms : this.#text.invalid_measurement || this.#emptyText;
    const output = (selector, value) => {
      const element = this.querySelector(selector);
      if (element) element.textContent = value;
    };
    output('[data-out-boxes]', state.valid ? this.#number(state.boxes, 0) : '—');
    output('[data-out-planks]', state.valid && state.planksPerBox ? this.#number(state.boxes * state.planksPerBox, 0) : '—');
    output('[data-out-coverage]', state.valid ? this.#number(state.totalCoverageSqft) : '—');
    output('[data-out-note]', '');
    const cost = this.querySelector('[data-cost-wrap]');
    if (cost) cost.hidden = !state.valid || state.priceCents <= 0;
    if (state.valid && state.priceCents > 0) {
      output('[data-out-cost]', new Intl.NumberFormat(this.#root.dataset.locale || 'en', { style: 'currency', currency: this.#root.dataset.currency || 'CAD' }).format(state.boxes * state.priceCents / 100));
    } else {
      output('[data-out-cost]', '—');
    }
    const qty = this.querySelector('[data-cart-qty]');
    if (qty) qty.value = state.valid ? state.boxes : '0';
    const add = this.querySelector('[data-add]');
    if (add) add.disabled = !state.valid || state.priceCents <= 0;
    output('[data-add-count]', state.valid ? `(${state.boxes})` : '');
    this.#draft = {
      handle: this.#root.dataset.handle, mode: this.#mode, units: this.#units, waste: project.waste,
      single: { area: this.#value('[data-area]'), length: this.#value('[data-length]'), width: this.#value('[data-width]') },
      rooms: rooms.map((row, index) => this.#roomState(row, index)),
    };
    this.#root.cavadorFlooringCalculation = state;
    this.#root.dispatchEvent(new CustomEvent('cavador:flooring-calculation-change', { bubbles: true, detail: state }));
  }

  #restoreProject(saved) {
    const project = restorableFlooringProject(saved, this.#root.dataset.handle, this.#model());
    if (!project) return false;
    this.#changeUnits(project.units);
    this.#mode = project.mode;
    this.querySelector('[data-waste-select]').value = String(project.waste);
    if (project.mode === 'rooms') {
      this.querySelector('[data-rooms]').replaceChildren();
      for (const room of project.rooms) this.#addRoom(room);
    } else {
      for (const key of ['area', 'length', 'width']) this.querySelector(`[data-${key}]`).value = project.rooms[0][key] ?? '';
    }
    this.#syncControls();
    this.#compute();
    return true;
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('cavador-flooring-calculator')) {
  customElements.define('cavador-flooring-calculator', CavadorFlooringCalculator);
}
